import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { CrownSimpleIcon, HandCoinsIcon } from "@phosphor-icons/react";

/**
 * Compact slider that lets a Bidorah Plus member redeem accrued credits
 * at checkout. Non-Plus users see a tasteful upsell card pointing at /plus.
 *
 * Props:
 *   user: current logged-in user doc (or null)
 *   priceUsd: number — gross item price
 *   value: current redeem amount in CENTS (lifted state)
 *   onChange: (cents:number) => void
 */
export default function PlusRedeemBox({ user, priceUsd, value, onChange }) {
  const balance = Number(user?.credit_balance_cents || 0);
  const priceCents = Math.round(Number(priceUsd || 0) * 100);
  const max = Math.max(0, Math.min(balance, priceCents - 100)); // leave $1 on card
  const isPlus = user?.plus_status === "active";

  const dollars = useMemo(() => (value / 100).toFixed(2), [value]);
  const newTotal = useMemo(
    () => Math.max(1.0, Number(priceUsd || 0) - value / 100),
    [priceUsd, value]
  );

  if (!user) return null;

  // Upsell non-Plus users
  if (!isPlus) {
    if (balance < 100) return null; // no meaningful balance — skip clutter
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs flex items-center gap-3"
        data-testid="plus-redeem-upsell"
      >
        <HandCoinsIcon size={20} className="text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-amber-900">
            You have ${(balance / 100).toFixed(2)} in Bidorah credits.
          </p>
          <p className="text-amber-700">
            <Link to="/plus" className="underline font-bold">
              Go Plus
            </Link>{" "}
            to redeem credits at checkout.
          </p>
        </div>
      </div>
    );
  }

  if (max <= 0) return null;

  return (
    <div
      className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-3"
      data-testid="plus-redeem-box"
    >
      <div className="flex items-center gap-2">
        <CrownSimpleIcon size={16} weight="fill" className="text-amber-500" />
        <p className="text-xs font-bold tracking-widest uppercase text-amber-900">
          Plus credit
        </p>
        <span className="ml-auto text-[11px] text-zinc-600">
          Balance ${(balance / 100).toFixed(2)}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={max}
          step={25}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-amber-500"
          data-testid="plus-redeem-slider"
        />
        <input
          type="number"
          min={0}
          max={max / 100}
          step={0.25}
          value={dollars}
          onChange={(e) =>
            onChange(Math.max(0, Math.min(max, Math.round(Number(e.target.value || 0) * 100))))
          }
          className="w-20 rounded-lg border border-zinc-300 px-2 py-1 text-sm text-right font-mono"
          data-testid="plus-redeem-input"
        />
      </div>
      {value > 0 && (
        <div
          className="mt-2 flex items-center justify-between text-xs"
          data-testid="plus-redeem-savings"
        >
          <span className="text-zinc-600">You'll pay on card</span>
          <span className="font-black text-emerald-700">
            ${newTotal.toFixed(2)}{" "}
            <span className="text-[10px] text-zinc-500 font-normal">
              (saved ${dollars})
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
